import { trigger, transition, style, animate } from '@angular/animations';
import { Component, OnInit, ChangeDetectionStrategy, EventEmitter, Output, Input, Injectable, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '../../base/base.component';
import { FileDataConnectorViewModel } from '../connectors/file/file-data-connector/file-data-connector.component';
import { StepModel } from '../dataTransformConfig/ingest/ingest-work-flow-layout/ingest-work-flow-layout.component';
import DatabaseType, { DataConnectorType, DataConnector, FileDataConnector, ExcelDataConnector, DatabaseConnector, FeatherDataConnector, OracleDataConnector, MySqlDataConnector, VerticaDataConnector, ParquetDataConnector } from '../../domainObjects/dataConnector';
import { DataSourceType } from '../../domainObjects/dataSourceType';
import { UserDataImportInfo, DataConnectorOptionItem, ConfigService } from '../../services/config/config.service';
import { ErrorReportingService } from '../../services/errorReporting/error-reporting.service';
import { DataConnectorMapping } from '../../domainObjects/dataConnectorMapping';
import { DataTransform } from '../../domainObjects/dataTransform';
import { DataConnectorViewModel } from '../../viewModels/dataConnectorViewModel';
import { DataTransformConfigViewModel } from '../../viewModels/dataTransformConfigViewModel';
import { TransformListViewModel } from '../transformer-list/transformer-list.component';
import { ColumnMapDefinitionModel } from '../../model/columnMapDefinitionModel';
import { ExcelDataConnectorComponent, ExcelDataConnectorViewModel } from '../connectors/excel/excel-data-connector/excel-data-connector.component';
import { DataFarmType } from '../../domainObjects/dataFarmType';
import { v4 as uuidv4 } from 'uuid';
import { UserDataService } from 'src/app/services/user-data-service';
import { DataTransformerConfig } from 'src/app/domainObjects/dataTransformConfig';
import { FeatherDataConnectorViewModel } from '../connectors/feather/feather.component';
import { DatabaseDataConnectorViewModel } from '../connectors/database/sql-data-connector/sql-data-connector.component';
import { OracleDataConnectorViewModel } from '../connectors/database/oracle-data-connector/oracle-data-connector.component';
import { MySqlDataConnectorViewModel } from '../connectors/database/mysql-data-connector/mysql-data-connector.component';
import { VerticaDataConnectorViewModel } from '../connectors/database/vertica-data-connector/vertica-data-connector.component';
import { ParquetDataConnectorViewModel } from '../connectors/parquet-data-connector/parquet-data-connector.component';

class DataSourceOptionItem {
  title: string;
  subtitle: string;
  icon: string;
  isSelected: boolean;
  dataSourceType: DataSourceType;

  constructor(dataSourceType: DataSourceType, title: string, subtitle: string, icon: string) {
    this.title = title;
    this.subtitle = subtitle;
    this.icon = icon;
    this.dataSourceType = dataSourceType;
  }
}


export class IngestConfigViewModel {
  guid: string;
  id: number;
  name: string;
  dataConnectorType?: DataConnectorType;
  databaseType?: DatabaseType;
  dataConnector?: DataConnectorViewModel<any> = null;
  columnMap?: ColumnMapDefinitionModel[];
  columns: string[];
  transforms: TransformListViewModel;
  targetDataFarm: DataFarmType;
  masterColumnMapGuid = '';
  matchAllColumns?: boolean ;
  notMatchingColumns?: string[] ;
  isMultiTabIngest?: boolean;
  excelTurboMode?: boolean;
  ingestAll?: boolean;
  sampleStart?: number;
  sampleEnd?: number;

  constructor(dataConnectorMapping: DataConnectorMapping, columns: string[],  transforms: DataTransform<any>[], dataTransformConfig?: DataTransformerConfig  ) {
    this.guid = dataConnectorMapping.guid;
    this.id = dataConnectorMapping.id;
    this.name = dataConnectorMapping.name;
    this.dataConnectorType = dataConnectorMapping.dataConnector?.dataConnectorType;
    this.databaseType = dataConnectorMapping.dataConnector?.databaseType;
    this.dataConnector = IngestConfigViewModel.createDataConnectorViewModel(dataConnectorMapping.dataConnector);
    this.targetDataFarm = dataConnectorMapping.targetDataFarm;
    this.masterColumnMapGuid = dataConnectorMapping.masterColumnMapGuid ?? '';
    let colIndex = 0;
    this.columnMap = [];
    this.isMultiTabIngest= dataConnectorMapping.isMultiTabIngest ?? false ;
    this.excelTurboMode=dataConnectorMapping.excelTurboMode ?? true;
    this.ingestAll = dataConnectorMapping.ingestAll ?? true;
    this.sampleStart = dataConnectorMapping.sampleStart ?? 1;
    this.sampleEnd = dataConnectorMapping.sampleEnd ?? 100;
  
    // console.log('import data IngestConfigViewModel dataTransformConfig', dataTransformConfig)

    // const configJsonObject = userData.getSelectedConfigJson(); //state.config;
    if(dataTransformConfig){
      var savedIngestConfig = dataTransformConfig.dataConnectorMappings.filter( item => item.guid === this.guid)[0];
    }

    // console.log('import data IngestConfigViewModel savedIngestConfig', savedIngestConfig)
    dataConnectorMapping.columnMap?.forEach( item => {
      
      const model = new ColumnMapDefinitionModel(
        item.id,
        colIndex++,
        item.sourceColumn,
        item.dataType,
        item.pyDataType,
        '',
        false,
        item.targetColumn);

      this.columnMap.push(model);

    });

    this.columns = columns;
    this.transforms = new TransformListViewModel(transforms);
  }

  private static createDataConnectorViewModel(dataConnector: DataConnector): DataConnectorViewModel<any> {
    if (!dataConnector) { return null; }
    switch (dataConnector.dataConnectorType) {
      case DataConnectorType.Text:
        return new FileDataConnectorViewModel(FileDataConnector.fromJSON(dataConnector));
      case DataConnectorType.Excel:
        return new ExcelDataConnectorViewModel(ExcelDataConnector.fromJSON(dataConnector));
      case DataConnectorType.Feather:
        return new FeatherDataConnectorViewModel(FeatherDataConnector.fromJSON(dataConnector));
      case DataConnectorType.Parquet:
        return new ParquetDataConnectorViewModel(ParquetDataConnector.fromJSON(dataConnector));
      case DataConnectorType.Database: {
        const dbType = (dataConnector as any).databaseType as DatabaseType;
        switch (dbType) {
          case DatabaseType.Oracle:
            return new OracleDataConnectorViewModel(OracleDataConnector.fromJSON(dataConnector) as any);
          case DatabaseType.MySql:
            return new MySqlDataConnectorViewModel(MySqlDataConnector.fromJSON(dataConnector) as any);
          case DatabaseType.Vertica:
            return new VerticaDataConnectorViewModel(VerticaDataConnector.fromJSON(dataConnector) as any);
          default:
            return new DatabaseDataConnectorViewModel(DatabaseConnector.fromJSON(dataConnector) as any);
        }
      }
      default:
        return null;
    }
  }

  syncToMasterMap(ingestConfig: IngestConfigViewModel): void {
    if (this.masterColumnMapGuid) {
      this.columnMap = ingestConfig.columnMap;
    }
  }

  // True when a connector-identifying value (file path, partition directory, table, query, etc.)
  // just changed on an already-mapped Ingest Farm — the existing Column Map still refers to the
  // old source, so callers should clear it and prompt the Analyst to reconfigure the mapper.
  hasMappedSourceChanged(previousValue: string, newValue: string): boolean {
    return !!previousValue && previousValue !== newValue && this.columnMap?.length > 0;
  }

  public getFileDataConnector(): FileDataConnectorViewModel {
    return this.dataConnector as FileDataConnectorViewModel;
  }

  public getExcelDataConnector(): ExcelDataConnectorViewModel {
    return this.dataConnector as ExcelDataConnectorViewModel;
  }

  public getDatabaseConnector(): DatabaseDataConnectorViewModel {
    return this.dataConnector as DatabaseDataConnectorViewModel;
  }
  
  public getFeatherDataConnector(): FeatherDataConnectorViewModel {
    return this.dataConnector as FeatherDataConnectorViewModel;
  }
   public getParquetDataConnector(): ParquetDataConnectorViewModel {
    return this.dataConnector as ParquetDataConnectorViewModel;
  }
  public getOracleConnector(): OracleDataConnectorViewModel {
    return this.dataConnector as OracleDataConnectorViewModel;
  }
  public getVerticaConnector(): VerticaDataConnectorViewModel {
    return this.dataConnector as VerticaDataConnectorViewModel;
  }
  public getMySqlConnector(): MySqlDataConnectorViewModel {
    return this.dataConnector as MySqlDataConnectorViewModel;
  }


  hasColumns(): boolean {
    return this.columnMap && this.columnMap.length > 0;
  }
}

export class DataBaseConnectorOptionItem {
  title: string;
  subtitle: string;
  databaseType: DatabaseType;
  icon: string;
  isSelected: boolean;

  constructor(connectorType: DatabaseType, title: string, subtitle: string, icon: string) {
    this.title = title;
    this.subtitle = subtitle;
    this.icon = icon;
    this.databaseType = connectorType;
  }
}

@Component({
    selector: 'app-import-data',
    templateUrl: './import-data.component.html',
    styleUrls: ['./import-data.component.css'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0, }),
                animate(1000, style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate(1000, style({ opacity: 0 }))
            ])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ImportDataComponent extends BaseComponent implements OnInit {
  @Output() stateChanged = new EventEmitter<DataTransformConfigViewModel>();
  @Input() stepModel: StepModel;
  @Input() viewModel: DataTransformConfigViewModel;
  @Input() ingestConfigViewModel: IngestConfigViewModel;
  @ViewChild(ExcelDataConnectorComponent) excelDataConnectorComponent!: ExcelDataConnectorComponent;

  // viewModel = new ImportDataSetupViewModel();
  // isMultiTabIngest = false;
  connectorType=""

  private lastUserImportInfoSubject = new BehaviorSubject<UserDataImportInfo>(null);
  lastUserImportInfo = this.lastUserImportInfoSubject.asObservable();
  selectedDataSourceType?: DataSourceOptionItem = null;
  selectedDataConnectorType?: DataConnectorOptionItem = null;
  selectedDatabaseConnectorType?: DataBaseConnectorOptionItem = null;

  dataConnectorType = DataConnectorType;
  databaseType=DatabaseType;

  items: DataSourceOptionItem[] = [
    new DataSourceOptionItem(DataSourceType.timeStampedData, 'Time Stamped Data', 'Import time stamped data', 'access_time'),
    new DataSourceOptionItem(DataSourceType.caseData, 'Case Data', 'Import case data', 'assignment_turned_in'),
    new DataSourceOptionItem(DataSourceType.userData, 'User Data', 'Import user data', 'account_circle'),
    new DataSourceOptionItem(DataSourceType.behaviorData, 'Behavior Data', 'Import behavior data', 'account_circle'),
    new DataSourceOptionItem(DataSourceType.activityData, 'Activity Data', 'Import activity data', 'account_circle'),
    new DataSourceOptionItem(DataSourceType.taskData, 'Task Data', 'Import activity data', 'account_circle'),
    new DataSourceOptionItem(DataSourceType.subtaskData, 'Subtask Data', 'Import activity data', 'account_circle'),
     new DataSourceOptionItem(DataSourceType.masterData, 'Master Data', 'Import activity data', 'account_circle')
  ];

  dataConnectors: DataConnectorOptionItem[] = [
    new DataConnectorOptionItem(DataConnectorType.Text, 'Text File', 'Import from a text or csv file', 'text_snippet'),
    new DataConnectorOptionItem(DataConnectorType.Excel, 'Excel', 'Import from an excel file', 'text_snippet'),
    new DataConnectorOptionItem(DataConnectorType.Feather, 'Feather', 'Import from a Feather file', 'text_snippet'),
    new DataConnectorOptionItem(DataConnectorType.Parquet, 'Parquet', 'Import from Parquet', 'dataset'),
    new DataConnectorOptionItem(DataConnectorType.Database, 'Database', 'Import from a Database', 'table_rows'),
    new DataConnectorOptionItem(DataConnectorType.RestApi, 'Rest API', 'Import from Rest API', 'cloud_download'),
   
    // new DataConnectorOptionItem(DataConnectorType.DistributedDatabase,
    //   'Document Database',
    //   'Import from a distributed document database',
    //   'text_snippet')
  ];

  dataBaseConnectors: DataBaseConnectorOptionItem[] = [
    new DataBaseConnectorOptionItem(DatabaseType.SQL, 'SQL Server', 'Import from a SQL Server', 'table_rows'),
    new DataBaseConnectorOptionItem(DatabaseType.Oracle, 'Oracle DB', 'Import from an Oracle', 'table_rows'),
    new DataBaseConnectorOptionItem(DatabaseType.MySql, 'My SQL', 'Import from a MySql', 'table_rows'),
    new DataBaseConnectorOptionItem(DatabaseType.Vertica, 'Vertica', 'Import from a Vertica', 'table_rows'),
    new DataBaseConnectorOptionItem(DatabaseType.Other, 'Other', 'Import from Other', 'table_rows'),
    // new DataConnectorOptionItem(DataConnectorType.DistributedDatabase,
    //   'Document Database',
    //   'Import from a distributed document database',
    //   'text_snippet')
  ];
  routerLinkBase = '../mapper/';
  routerLink = '';

  savedIngestConfig:any;

  constructor(
    logger: ErrorReportingService,
    dialog: MatDialog,
    private mapperService: ConfigService,
    private userData: UserDataService) {
    super(logger, dialog);
  }

  async ngOnInit(): Promise<void> {
    const organizationId = 1;   // TODO: Get from AppViewModel
    //console.log('loading Data Connector...');
    //console.log("this.viewModel.dataConnectorType",this.viewModel.dataConnectorType);
    
    // const userDataInfo = await this.mapperService.getLastUserDataImportInfo(organizationId);
    // if (userDataInfo) {
    //   //console.log('Got userdataInfo: ' + JSON.stringify(userDataInfo));
    //   this.lastUserImportInfoSubject.next(userDataInfo);
    // }
 
    const data = history.state.data;
    // console.log('import data history state', this.userData.getSelectedConfigJson());

    console.log('import ingestConfigViewModel', this.ingestConfigViewModel);

    this.selectedDataSourceType = this.items.filter( item => item.dataSourceType === this.viewModel.dataSourceType)[0];

    //console.log('import selectedDataSourceType', this.selectedDataSourceType);
    const configJsonObject = this.userData.getSelectedConfigJson(); //state.config;
    if(configJsonObject){
      this.savedIngestConfig = configJsonObject.dataConnectorMappings.filter( item => item.guid === this.ingestConfigViewModel?.guid)[0];
    }
    // console.log('import savedIngestConfig', this.savedIngestConfig?.dataConnector?.dataConnectorType);
    

    this.selectedDataConnectorType =  this.dataConnectors.filter(i =>
      i.dataConnectorType === this.ingestConfigViewModel.dataConnectorType )[0];
    
       


  }

  ngOnChanges(changes: any): void {
    

    const configJsonObject = this.userData.getSelectedConfigJson(); //state.config;
    if(configJsonObject){
      this.savedIngestConfig = configJsonObject.dataConnectorMappings.filter( item => item.guid === this.ingestConfigViewModel?.guid)[0];
    }
    
    // console.log('import-data-compo changes',changes);
    // console.log('import savedIngestConfig', this.savedIngestConfig);
    // console.log('import-data-compo connector: ', connector)



    if (changes.ingestConfigViewModel) {
      if (changes.ingestConfigViewModel.firstChange === true || !changes.ingestConfigViewModel.previousValue) {
        return;
      }

      if (changes.ingestConfigViewModel.previousValue === changes.ingestConfigViewModel.currentValue) {
        return;
      }
    }

    // this.showConfirmDialog('Save Changes', 
    // `Would you like to keep your changes made to ${changes.ingestConfig.previousValue.name}?`, (confirmed) => {

    //   if (confirmed) {
    //     this.ingestConfig.dataConnectorType = this.selectedDataConnectorType.dataConnectorType;

    //     if (this.selectedDataConnectorType.dataConnectorType === DataConnectorType.Text) {
    //       this.ingestConfig.dataConnector = this.viewModel.fileDataConnectorViewModel;
    //     } else if (this.selectedDataConnectorType.dataConnectorType === DataConnectorType.Database) {
    //     }
    //   }
    // });
  }

  onDataSourceTypeSelected(item: DataSourceOptionItem): void {
    // console.log('within the function onDataSourceTypeSelected : ', item)
    this.items.forEach( option => option.isSelected = false );
    item.isSelected = true;
    this.selectedDataSourceType = item;
    this.viewModel.dataSourceType = item.dataSourceType;

    this.routerLink = `${this.routerLinkBase}${this.selectedDataSourceType.dataSourceType}`;
    this.stateChanged.emit(this.viewModel);
  }

  onToggleChange(value: boolean) {
    console.log('Multi-tab Ingest toggle changed to:', value);
    this.excelDataConnectorComponent.toggleChange(value);
   // this.viewModel.
    //this.stateChanged.emit(this.viewModel);
    // Perform additional actions based on the new value
  }
  onDataConnectorSelected(connector: DataConnectorOptionItem): void {
  //  console.log("connector prajna",connector.title);
    this.connectorType=connector.title;
    // const configJsonObject = this.userData.getSelectedConfigJson(); //state.config;
    // console.log('within the function onDataConnectorSelected : ', connector)
    // console.log('import selectedDataConnectorType', this.ingestConfigViewModel);

    // connector.dataConnectorType = 4
    // if(configJsonObject){
    //   var savedIngestConfig = configJsonObject.dataConnectorMappings.filter( item => item.guid === this.ingestConfigViewModel?.guid)[0];
    //   console.log('import savedIngestConfig', savedIngestConfig);

    //   if(connector.dataConnectorType != savedIngestConfig.dataConnector.dataConnectorType){

    //     this.ingestConfigViewModel.dataConnectorType  = 4;
    //     this.viewModel.dataConnectorType = 4;
    //     this.stateChanged.emit(this.viewModel);
        
    //     this.displayInfo("Cannot change the Data Connector Type for this Ingest Farm.  Add a new Ingest Farm to do so.",'Info','');
    //     return ;
    //   }
    // }

    this.dataConnectors.forEach( option => option.isSelected = false );
    connector.isSelected = true;
    this.selectedDataConnectorType = connector;
    this.viewModel.dataConnectorType = connector.dataConnectorType;

    this.ingestConfigViewModel.dataConnectorType = connector.dataConnectorType;

    this.viewModel.isDataValid = true;
    this.stepModel.isComplete = true;
    // console.log('check the viewModel : ', this.viewModel)
    this.stateChanged.emit(this.viewModel);
  }
  onDataBaseConnectorSelected(connector: DataBaseConnectorOptionItem): void {
   // console.log("connector prajna",connector.title);
    //this.connectorType=connector.title;
    this.dataBaseConnectors.forEach( option => option.isSelected = false );
    connector.isSelected = true;
    this.selectedDatabaseConnectorType = connector;
    this.viewModel.databaseType = connector.databaseType;

    this.ingestConfigViewModel.databaseType = connector.databaseType;

    this.viewModel.isDataValid = true;
    this.stepModel.isComplete = true;
    // console.log('check the viewModel : ', this.viewModel)
    this.stateChanged.emit(this.viewModel);
  }
  delay(ms: number): Promise<void> {
    return new Promise( resolve => setTimeout(resolve, ms) );
  }

  // tslint:disable-next-line:typedef
  async scroll(el: HTMLElement) {
    await delay(2000);
    el.scrollIntoView({behavior: 'smooth'});
  }
}
